<template>
  <v-container fluid class="pa-6">
    <v-overlay color="primary" :absolute="true" v-if="loading">
      <v-progress-circular
        indeterminate
        color="accent"
        size="64"
      ></v-progress-circular>
    </v-overlay>

    <v-row class="mb-2">
      <v-col cols="12">
        <div class="text-h5 font-weight-bold">Command Center</div>
        <div class="text-body-2 text--secondary">
          Your talent intelligence overview at a glance
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="6" md="3">
        <dashboard-card
          :color="$vuetify.theme.dark ? '#5b9fff' : '#2b7fff'"
          :colorLight="$vuetify.theme.dark ? '#1a2a4a' : '#dfecff'"
          title="Total Employees"
          :headlineData="displayProfileCount"
          subText="Active profiles"
          iconText="mdi-account-multiple-outline"
          link="/Company/Intelligence/People"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <dashboard-card
          :color="$vuetify.theme.dark ? '#a78bfa' : '#7c3aed'"
          :colorLight="$vuetify.theme.dark ? '#2d2250' : '#ede9fe'"
          title="Searches"
          :headlineData="totalSearches"
          subText="Last 30 days"
          iconText="mdi-magnify"
          link="/Company/Intelligence/InsightsHub?tab=analytics"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <dashboard-card
          :color="$vuetify.theme.dark ? '#34d399' : '#00c950'"
          :colorLight="$vuetify.theme.dark ? '#1a3a2a' : '#d1ffe3'"
          title="Hidden Leaders"
          :headlineData="hiddenLeadersCount"
          subText="Identified talent"
          iconText="mdi-star-outline"
          link="/Company/Intelligence/InsightsHub?tab=intelligence"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <dashboard-card
          :color="$vuetify.theme.dark ? '#ffab76' : '#ff6900'"
          :colorLight="$vuetify.theme.dark ? '#3d2a1a' : '#ffe9d9'"
          title="Succession Gaps"
          :headlineData="successionGapsCount"
          subText="Roles needing coverage"
          iconText="mdi-alert-circle-outline"
          link="/Company/Intelligence/InsightsHub?tab=intelligence"
        />
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <div class="text-subtitle-1 font-weight-bold mb-2">Quick Actions</div>
        <dashboard-quick-actions @navigate="nav" />
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <general-card>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
            <v-icon left color="primary" small>mdi-star-outline</v-icon>
            Top Hidden Leaders
          </v-card-title>
          <v-card-text>
            <div v-if="hiddenLeaders.length === 0" class="text-body-2 text--secondary">
              No hidden leader data available yet. Run Talent Intelligence to discover hidden leaders.
            </div>
            <v-list dense v-else>
              <v-list-item
                v-for="(leader, idx) in hiddenLeaders.slice(0, 5)"
                :key="idx"
                class="px-0 clickable-list-item"
                @click="navigateToProfile(leader)"
              >
                <v-list-item-avatar size="32" :color="$vuetify.theme.dark ? '#2d2250' : '#ede9fe'">
                  <v-icon small :color="$vuetify.theme.dark ? '#b39ddb' : '#7c3aed'">mdi-account</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ leader.name || leader.profileName || 'Unknown' }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ leader.current_title || leader.title || leader.currentTitle || '' }}
                    <span v-if="leader.signal_count"> &middot; {{ leader.signal_count }} signals</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon small color="grey">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </general-card>
      </v-col>

      <v-col cols="12" md="6">
        <general-card>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
            <v-icon left color="warning" small>mdi-alert-circle-outline</v-icon>
            Succession Gaps
          </v-card-title>
          <v-card-text>
            <div v-if="successionGaps.length === 0" class="text-body-2 text--secondary">
              No succession gap data available yet. Run Talent Intelligence to identify gaps.
            </div>
            <v-list dense v-else>
              <v-list-item
                v-for="(gap, idx) in successionGaps.slice(0, 5)"
                :key="idx"
                class="px-0 clickable-list-item"
                @click="navigateToSuccession(gap)"
              >
                <v-list-item-avatar size="32" :color="$vuetify.theme.dark ? '#3d2a1a' : '#ffe9d9'">
                  <v-icon small :color="$vuetify.theme.dark ? '#ffab76' : '#ff6900'">mdi-swap-vertical</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ gap.level || gap.role || gap.title || 'Gap' }} → {{ gap.bench_level || gap.benchLevel || '' }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    Ratio {{ gap.ratio || '—' }}:1
                    <span v-if="gap.severity"> &middot; {{ gap.severity }}</span>
                    <span v-if="gap.current_count || gap.currentCount"> &middot; {{ gap.current_count || gap.currentCount }} positions</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon small color="grey">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions v-if="successionGaps.length > 0" class="px-4 pb-3 pt-0">
            <v-spacer />
            <v-btn
              small
              color="#ff6900"
              outlined
              @click="$router.push({ name: 'IntelligenceFindTalent', query: { tab: 'succession' } })"
            >
              <v-icon small left>mdi-account-switch</v-icon>
              Analyze Pipeline
            </v-btn>
          </v-card-actions>
        </general-card>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <general-card>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-1 d-flex align-center">
            <v-icon left color="#7c3aed" small>mdi-radar</v-icon>
            Talent Density Radar
            <v-spacer />
            <v-select
              v-model="radarFunctionalArea"
              :items="radarFunctionalAreas"
              dense
              outlined
              hide-details
              style="max-width: 220px"
              class="text-body-2"
              @change="loadRadarData"
            />
          </v-card-title>
          <v-card-subtitle class="text-caption pb-2">
            How strong is your talent bench? Each dimension scores 0-100 based on real profile data.
            <span v-if="radarProfileCount" class="ml-1">&middot; {{ radarProfileCount }} profiles analyzed</span>
          </v-card-subtitle>
          <v-card-text>
            <div v-if="radarLoading" class="text-center py-6">
              <v-progress-circular indeterminate color="#7c3aed" size="40" />
            </div>
            <div v-else-if="radarDimensions.length === 0" class="text-body-2 text--secondary text-center py-4">
              No radar data available yet.
            </div>
            <div v-else>
              <div class="d-flex align-start flex-wrap" style="gap: 24px">
                <div style="flex: 1; min-width: 280px; max-width: 500px; margin: 0 auto">
                  <canvas ref="radarCanvas" width="460" height="380"></canvas>
                </div>
                <div style="min-width: 260px; max-width: 340px" class="pt-2">
                  <div
                    v-for="dim in radarDimensions"
                    :key="dim.dimension"
                    class="mb-3"
                  >
                    <div class="d-flex align-center mb-1">
                      <div style="width: 150px" class="text-caption font-weight-medium text-truncate">{{ dim.dimension }}</div>
                      <div style="flex: 1; margin: 0 8px">
                        <div
                          style="height: 6px; border-radius: 3px; background: rgba(124,58,237,0.12)"
                        >
                          <div
                            :style="{
                              width: dim.score + '%',
                              height: '100%',
                              borderRadius: '3px',
                              background: dim.score >= 70 ? '#34d399' : dim.score >= 45 ? '#F59E0B' : '#ef4444',
                            }"
                          ></div>
                        </div>
                      </div>
                      <div
                        class="text-caption font-weight-bold"
                        :style="{ color: dim.score >= 70 ? '#34d399' : dim.score >= 45 ? '#F59E0B' : '#ef4444' }"
                        style="min-width: 32px; text-align: right"
                      >
                        {{ dim.score }}
                      </div>
                    </div>
                    <div class="text-caption grey--text text--darken-1" style="padding-left: 2px; line-height: 1.3">
                      {{ radarDimensionDescriptions[dim.dimension] || '' }}
                    </div>
                  </div>
                </div>
              </div>

              <v-divider class="my-3" />

              <div class="d-flex align-start flex-wrap" style="gap: 16px">
                <div v-if="radarStrongest" class="d-flex align-center" style="flex: 1; min-width: 200px">
                  <v-icon small color="#34d399" class="mr-2">mdi-arrow-up-bold-circle</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold" style="color: #34d399">Strongest: {{ radarStrongest.dimension }} ({{ radarStrongest.score }})</div>
                    <div class="text-caption grey--text text--darken-1">{{ radarStrongestInsight }}</div>
                  </div>
                </div>
                <div v-if="radarWeakest" class="d-flex align-center" style="flex: 1; min-width: 200px">
                  <v-icon small color="#ef4444" class="mr-2">mdi-arrow-down-bold-circle</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold" style="color: #ef4444">Needs Attention: {{ radarWeakest.dimension }} ({{ radarWeakest.score }})</div>
                    <div class="text-caption grey--text text--darken-1">{{ radarWeakestInsight }}</div>
                  </div>
                </div>
                <div class="d-flex align-center" style="flex: 1; min-width: 200px">
                  <v-icon small color="#7c3aed" class="mr-2">mdi-information-outline</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold" style="color: #7c3aed">Overall Balance: {{ radarBalanceLabel }}</div>
                    <div class="text-caption grey--text text--darken-1">{{ radarBalanceInsight }}</div>
                  </div>
                </div>
              </div>

              <div class="mt-3 pa-2 rounded" style="background: rgba(124,58,237,0.04); border: 1px solid rgba(124,58,237,0.1)">
                <div class="text-caption grey--text text--darken-2">
                  <v-icon x-small class="mr-1" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                  <strong>How to read this:</strong> Scores are computed from actual profile data — skills listed, STAR achievements documented, credentials held, and career progression. Green (70+) = strong bench. Amber (45-69) = developing. Red (&lt;45) = gap to address. Use the dropdown to compare functional areas.
                </div>
              </div>
            </div>
          </v-card-text>
        </general-card>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <general-card>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-1">
            <v-icon left color="primary" small>mdi-history</v-icon>
            Recent Search Activity
          </v-card-title>
          <v-card-text>
            <div v-if="recentSearches.length === 0" class="text-body-2 text--secondary">
              No recent search activity recorded.
            </div>
            <v-simple-table v-else dense>
              <thead>
                <tr>
                  <th class="text-left">Query</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Date</th>
                  <th class="text-left">Results</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(search, idx) in recentSearches.slice(0, 8)" :key="idx">
                  <td class="text-body-2">{{ search.query || search.searchQuery || '—' }}</td>
                  <td class="text-body-2">{{ search.type || search.searchType || '—' }}</td>
                  <td class="text-body-2">{{ formatDate(search.timestamp || search.date) }}</td>
                  <td class="text-body-2">{{ search.resultCount != null ? search.resultCount : '—' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
          <v-card-actions class="px-4 pb-3 pt-0">
            <v-spacer />
            <v-btn
              small
              color="primary"
              outlined
              @click="$router.push('/Company/Intelligence/InsightsHub?tab=analytics')"
            >
              <v-icon small left>mdi-chart-box-outline</v-icon>
              View Analytics
            </v-btn>
          </v-card-actions>
        </general-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import * as companyApi from "../../api/CompanyApi";
import DashboardCard from "../../components/Company/DashboardCard.vue";
import DashboardQuickActions from "../../components/Company/DashboardQuickActions.vue";
import GeneralCard from "../../components/Company/GeneralCard.vue";

export default {
  name: "CompanyDashboard",
  components: {
    DashboardCard,
    DashboardQuickActions,
    GeneralCard,
  },
  data: () => ({
    totalEmployees: 0,
    totalProfiles: 0,
    totalAdmins: 0,
    loading: false,
    radarDimensions: [],
    radarFunctionalArea: "All",
    radarFunctionalAreas: ["All"],
    radarProfileCount: 0,
    radarLoading: false,
    radarChartInstance: null,
  }),
  computed: {
    ...mapGetters("intelligenceTalent", {
      hiddenLeaders: "hiddenLeaders",
      successionGaps: "successionGaps",
    }),
    ...mapGetters("intelligenceAnalytics", {
      searchesOverTime: "searchesOverTime",
    }),
    displayProfileCount() {
      return this.totalProfiles > 0 ? this.totalProfiles : this.totalEmployees;
    },
    totalSearches() {
      if (!this.searchesOverTime || this.searchesOverTime.length === 0) return 0;
      return this.searchesOverTime.reduce(
        (sum, entry) => sum + (entry.count || entry.value || 0),
        0
      );
    },
    hiddenLeadersCount() {
      return this.hiddenLeaders ? this.hiddenLeaders.length : 0;
    },
    successionGapsCount() {
      return this.successionGaps ? this.successionGaps.length : 0;
    },
    recentSearches() {
      const log = this.$store.state.intelligenceAnalytics?.auditLog;
      return log || [];
    },
    radarDimensionDescriptions() {
      return {
        'Leadership Depth': 'People in leadership roles with STAR evidence of leading teams or initiatives',
        'Technical Breadth': 'Range and depth of technical skills across the organization',
        'Domain Coverage': 'How many distinct business domains are represented in career histories',
        'STAR Evidence Quality': 'Completeness of Situation-Task-Action-Result documentation',
        'Succession Readiness': 'Bench strength for critical roles based on skill and seniority fit',
        'Skills Currency': 'Proficiency levels and recency of documented skills',
        'Credential Coverage': 'Percentage of profiles with professional certifications or credentials',
        'Scope & Scale': 'Evidence of enterprise, cross-functional, or global experience in STAR stories',
      };
    },
    radarValidDimensions() {
      return this.radarDimensions.filter(d => typeof d.score === 'number' && isFinite(d.score));
    },
    radarStrongest() {
      if (!this.radarValidDimensions.length) return null;
      return [...this.radarValidDimensions].sort((a, b) => b.score - a.score)[0];
    },
    radarWeakest() {
      if (!this.radarValidDimensions.length) return null;
      return [...this.radarValidDimensions].sort((a, b) => a.score - b.score)[0];
    },
    radarStrongestInsight() {
      if (!this.radarStrongest) return '';
      const d = this.radarStrongest.dimension;
      const s = this.radarStrongest.score;
      if (d === 'STAR Evidence Quality') return s >= 90 ? 'Profiles are exceptionally well-documented.' : 'Most profiles have solid STAR documentation.';
      if (d === 'Leadership Depth') return 'Strong leadership bench with proven track records.';
      if (d === 'Technical Breadth') return 'Wide range of technical capabilities across teams.';
      if (d === 'Succession Readiness') return 'Good bench strength for key roles.';
      if (d === 'Skills Currency') return 'Skills are up-to-date and well-documented.';
      return 'This area is a competitive advantage for the organization.';
    },
    radarWeakestInsight() {
      if (!this.radarWeakest) return '';
      const d = this.radarWeakest.dimension;
      const s = this.radarWeakest.score;
      if (d === 'Credential Coverage') return s < 30 ? 'Few profiles have credentials. Encourage certification tracking.' : 'Consider a credential documentation initiative.';
      if (d === 'Domain Coverage') return 'Career histories may need more variety or detail.';
      if (d === 'Scope & Scale') return 'Few profiles document enterprise or global experience.';
      if (d === 'Technical Breadth') return 'Technical skill documentation could be broader.';
      if (d === 'Succession Readiness') return 'Bench strength is thin. Review succession plans.';
      return s < 30 ? 'This area needs focused improvement.' : 'Room for growth here.';
    },
    radarBalanceLabel() {
      if (!this.radarValidDimensions.length) return '';
      const scores = this.radarValidDimensions.map(d => d.score);
      const spread = Math.max(...scores) - Math.min(...scores);
      if (spread <= 20) return 'Well-Balanced';
      if (spread <= 40) return 'Moderate Spread';
      return 'Uneven';
    },
    radarBalanceInsight() {
      if (!this.radarValidDimensions.length) return '';
      const scores = this.radarValidDimensions.map(d => d.score);
      const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      const spread = Math.max(...scores) - Math.min(...scores);
      if (spread <= 20) return 'Capabilities are evenly distributed (avg ' + avg + '). No single weak spot.';
      if (spread <= 40) return 'Some variation across dimensions (avg ' + avg + '). Focus on the lowest areas.';
      return 'Large gap between strongest and weakest (spread: ' + spread + '). Prioritize the red areas.';
    },
  },
  mounted() {
    this.loadAllData();
  },
  methods: {
    nav(routerPath) {
      this.$router.push({ path: routerPath });
    },
    navigateToProfile(leader) {
      const name = leader.name || leader.profileName || '';
      if (name) {
        this.$router.push({
          name: 'IntelligenceSageHR',
          query: { prefill: 'Tell me about ' + name }
        });
      } else {
        this.$router.push({ name: 'IntelligenceTalentInsights' });
      }
    },
    navigateToSuccession(gap) {
      const role = gap.level || gap.role || gap.title || '';
      this.$router.push({
        name: 'IntelligenceFindTalent',
        query: { tab: 'succession', role: role }
      });
    },
    navigateToInsights(section) {
      this.$router.push({
        name: 'IntelligenceTalentInsights',
        query: { section: section }
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },
    async loadAllData() {
      this.loading = true;
      try {
        const statsPromise = companyApi.GetEmployeeStats().then((result) => {
          if (result.success) {
            this.totalEmployees = result.data?.totalEmployees || 0;
            this.totalProfiles = result.data?.totalProfiles || 0;
            this.totalAdmins = result.data?.totalAdmins || 0;
          }
        });

        const talentPromise = this.$store.state.intelligenceTalent?.intelligence
          ? Promise.resolve()
          : this.$store
              .dispatch("intelligenceTalent/loadTalentIntelligence")
              .catch(() => {});

        const analyticsPromise = this.$store.state.intelligenceAnalytics?.analytics
          ? Promise.resolve()
          : this.$store
              .dispatch("intelligenceAnalytics/loadAnalytics", 30)
              .catch(() => {});

        const auditPromise =
          this.$store.state.intelligenceAnalytics?.auditLog?.length > 0
            ? Promise.resolve()
            : this.$store
                .dispatch("intelligenceAnalytics/loadAuditLog", 30)
                .catch(() => {});

        await Promise.all([statsPromise, talentPromise, analyticsPromise, auditPromise]);
        this.loadRadarData();
      } finally {
        this.loading = false;
      }
    },
    async loadRadarData() {
      this.radarLoading = true;
      try {
        const param = this.radarFunctionalArea && this.radarFunctionalArea !== 'All' ? `?functionalArea=${encodeURIComponent(this.radarFunctionalArea)}` : '';
        const resp = await companyApi.GetGeneric(`/api/GetTalentRadar${param}`);
        const data = resp?.success ? resp.data : resp;
        if (data) {
          this.radarDimensions = data.dimensions || [];
          this.radarProfileCount = data.profileCount || 0;
          const areas = data.functionalAreas || [];
          this.radarFunctionalAreas = ['All', ...areas.filter(a => a !== 'All')];
          this.$nextTick(() => this.renderRadarChart());
        }
      } catch (e) {
        console.warn('Radar load failed:', e);
      } finally {
        this.radarLoading = false;
      }
    },
    async loadChartJs() {
      if (window.Chart) return;
      return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src*="chart.js"]') || document.querySelector('script[src*="Chart"]');
        if (existing) { resolve(); return; }
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load Chart.js'));
        document.head.appendChild(script);
      });
    },
    async renderRadarChart() {
      if (!this.radarDimensions.length || !this.$refs.radarCanvas) return;
      try {
        await this.loadChartJs();
      } catch { return; }
      if (this.radarChartInstance) {
        this.radarChartInstance.destroy();
        this.radarChartInstance = null;
      }
      const ctx = this.$refs.radarCanvas.getContext('2d');
      const labels = this.radarDimensions.map(d => d.dimension);
      const scores = this.radarDimensions.map(d => d.score);
      const isDark = this.$vuetify.theme.dark;
      this.radarChartInstance = new window.Chart(ctx, {
        type: 'radar',
        data: {
          labels,
          datasets: [{
            label: this.radarFunctionalArea || 'All Functions',
            data: scores,
            backgroundColor: isDark ? 'rgba(124, 58, 237, 0.25)' : 'rgba(124, 58, 237, 0.15)',
            borderColor: '#7c3aed',
            borderWidth: 2,
            pointBackgroundColor: scores.map(s => s >= 70 ? '#34d399' : s >= 45 ? '#F59E0B' : '#ef4444'),
            pointBorderColor: '#fff',
            pointBorderWidth: 1,
            pointRadius: 5,
            pointHoverRadius: 7,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            r: {
              beginAtZero: true,
              max: 100,
              ticks: {
                stepSize: 25,
                color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
                backdropColor: 'transparent',
                font: { size: 10 },
              },
              grid: {
                color: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              },
              angleLines: {
                color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              },
              pointLabels: {
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                font: { size: 11 },
              },
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.label}: ${ctx.raw}/100`
              }
            }
          }
        }
      });
    },
  },
  beforeDestroy() {
    if (this.radarChartInstance) {
      this.radarChartInstance.destroy();
    }
  },
};
</script>

<style lang="css" scoped>
.icon-circle {
  color: white;
  background-color: #0059a3;
  border-radius: 50%;
  padding: 10px;
}

.headline-text {
  font-size: 24px;
  font-weight: 500;
}

.clickable-list-item {
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}
.clickable-list-item:hover {
  background-color: rgba(0, 122, 255, 0.06);
}
.theme--dark .clickable-list-item:hover {
  background-color: rgba(255, 255, 255, 0.06);
}
</style>
